import React, { useState, useRef, useEffect } from 'react';
import { Search, X, RefreshCw } from 'lucide-react';
import { loadCatalog, searchCatalog, CATALOG_BRAND_COLORS } from './ItemAutocomplete';

/**
 * CatalogProductPicker — pick products from the manufacturer catalogs for a
 * design (Marketing Studio composer + FlyerBuilder).
 *
 * Search is by product name or item number, over the SAME cached catalog and
 * with the SAME ranking as the receipt autocomplete (ItemAutocomplete.jsx).
 * The catalog loads on first focus, not on mount — it is ~28k rows and most
 * designs never open this.
 *
 * Only { sku, name, brand, image } is kept per pick. The full record (maker's
 * copy, materials, dimensions, full-size photo) is read server-side by
 * /api/generate-flyer from the sku + brand, so the design is built from the
 * database and not from this component's state.
 *
 * Props:
 *   products  - current picks: [{ sku, name, brand, image }]
 *   onChange  - (nextProducts) => void
 *   max       - most picks allowed right now (the parent subtracts attached
 *               photos: a design takes 4 images in total)
 *   disabled  - lock the control (e.g. while generating)
 */
const keyOf = (p) => `${(p.sku || '').toUpperCase()}|${p.brand || ''}`;

const CatalogProductPicker = ({ products = [], onChange, max = 4, disabled = false }) => {
  const [catalog, setCatalog] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const ensureCatalog = () => {
    if (catalog || loading) return;
    setLoading(true);
    loadCatalog()
      .then((rows) => { if (aliveRef.current) setCatalog(rows); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
  };

  const isFull = products.length >= max;
  const picked = new Set(products.map(keyOf));
  // Over-fetch so already-picked rows can be dropped without shortening the list.
  const matches = catalog
    ? searchCatalog(catalog, query, 8 + products.length).filter((p) => !picked.has(keyOf(p))).slice(0, 8)
    : [];
  const typed = query.trim().length >= 2;
  const showDropdown = open && typed && !isFull;

  const choose = (p) => {
    if (isFull) return;
    onChange([...products, { sku: p.sku, name: p.name, brand: p.brand, image: p.image || null }]);
    setQuery('');
    setOpen(false);
    setHighlight(0);
  };

  const remove = (p) => onChange(products.filter((x) => keyOf(x) !== keyOf(p)));

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!showDropdown || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(matches[highlight] || matches[0]);
    }
  };

  return (
    <div ref={wrapRef} className="space-y-1.5">
      {/* Picked products */}
      {products.length > 0 && (
        <ul className="space-y-1">
          {products.map((p) => (
            <li
              key={keyOf(p)}
              className="flex items-center gap-2.5 pl-1.5 pr-1 py-1.5 rounded-xl bg-white border border-slate-200"
            >
              {p.image ? (
                <img src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0 bg-slate-100" />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium text-slate-900 truncate">{p.name || p.sku}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[11px] text-slate-500 font-mono truncate">{p.sku}</span>
                  {p.brand && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${CATALOG_BRAND_COLORS[p.brand] || 'bg-slate-100 text-slate-600'}`}>
                      {p.brand}
                    </span>
                  )}
                  {!p.image && (
                    <span className="text-[10px] text-slate-400 flex-shrink-0">no photo on file</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(p)}
                disabled={disabled}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0 disabled:opacity-50"
                aria-label={`Remove ${p.name || p.sku}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        {/* text-base on phones prevents iOS auto-zoom on focus. */}
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => { ensureCatalog(); if (query.trim().length >= 2) setOpen(true); }}
          onKeyDown={onKeyDown}
          disabled={disabled || isFull}
          placeholder={isFull
            ? 'Design is full — remove a product or photo to add another'
            : products.length > 0
              ? 'Add another product — name or item #'
              : 'Add a product from your catalog — name or item #'}
          className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-base md:text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-400 transition-colors disabled:bg-slate-50 disabled:text-slate-400"
          autoComplete="off"
          aria-label="Search the product catalog by name or item number"
        />

        {showDropdown && (
          <ul
            className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {!catalog ? (
              <li className="flex items-center gap-2 px-3 py-3 text-[12px] text-slate-500">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Loading catalog…
              </li>
            ) : matches.length === 0 ? (
              <li className="px-3 py-3 text-[12px] text-slate-500">
                No catalog product matches &ldquo;{query.trim()}&rdquo;. You can still attach your own photo.
              </li>
            ) : (
              matches.map((p, i) => (
                <li
                  key={keyOf(p) + i}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus, prevent blur-close before pick
                    choose(p);
                  }}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${i === highlight ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                >
                  {p.image ? (
                    <img src={p.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 bg-slate-100" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-slate-100 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">{p.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 font-mono">{p.sku}</span>
                      {p.brand && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${CATALOG_BRAND_COLORS[p.brand] || 'bg-slate-100 text-slate-600'}`}>
                          {p.brand}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CatalogProductPicker;
